/* eslint-disable max-lines */
/* eslint-disable max-lines-per-function */
import * as React from 'react';
import AppTheme from '../../../theme/AppTheme';
import Box from '@mui/material/Box';
import { brand } from '../../../theme/themePrimitives';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import CustomInput from '../../../components/reusable/input';
import ErrorModal from '../../../components/modals/error_modal';
import ImageClass from '../../../assets';
import LabelWrapper from '../../../components/wrapper/label';
import PasswordValidation from '../../../components/validation/password';
import { RouteChangeToastCleaner } from '../../../route_toast_cleaner';
import { SignUpParams } from '../../../params/auth';
import { signupSchema } from '../../../schema/auth_schema';
import { signupThunk } from '../../../redux/thunks';
import Typography from '@mui/material/Typography';
import { AppDispatch, RootState } from '../../../redux/store';
import { Avatar, CircularProgress } from '@mui/material';
import { Form, Formik } from 'formik';
import { styled, useTheme } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';

/**
 * @heading1: Singup Page
 * @paragraph: This is the Signup page component.
 * @heading4: Styled Components.
 * @ol:
 * Styled page
 * Page Container
 * Image Container
 * Form Container
 * Styled Form
 * @separator:
 */
const StyledPage = styled(Box)(({ theme }) => ({
  padding: '2rem 5rem',
  display: 'flex',
  height: '100vh',
  [theme.breakpoints.down('sm')]: {
    padding: '5rem 0'
  }
}));

const StyledLink = styled('span')(() => ({
  'cursor': 'pointer',
  'color': brand[300],
  'whiteSpace': 'nowrap',
  '&:hover': {
    textDecoration: 'underline'
  }
}));
const PageContainer = styled(Box)(({ theme }) => ({
  flex: '1',
  display: 'flex',
  // height: '100vh',
  borderRadius: '10px',
  // boxShadow: '1px 1px 1px 1px #f4f4f4',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column'
  }
}));

const ImageContainer = styled(Box)(({ theme }) => ({
  flex: 7,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: brand[300],
  borderTopLeftRadius: '10px',
  borderBottomLeftRadius: '10px',
  [theme.breakpoints.down('sm')]: {
    height: '50%',
    display: 'none'
  }
}));

const FormContainer = styled(Box)(({ theme }) => ({
  flex: 3,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  width: '100%',
  borderTop: '1px solid #ededed',
  borderRight: '1px solid #ededed',
  borderBottom: '1px solid #ededed',
  borderTopRightRadius: '10px',
  borderBottomRightRadius: '10px',
  padding: theme.spacing(4),
  [theme.breakpoints.down('sm')]: {
    height: '50%'
  }
}));

const StyledForm = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: '1200px',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2)
}));

/**
 * @heading2: Signup Page Component
 * @heading3: Variables
 * @ol:
 * initialValues : {SignUpParams} - signup initial values.
 * dispatch : {AppDispatch} - The Redux dispatch function for triggering state updates.
 * navigate - Navigate for routing purpose.
 * isLoading - getting isLoading from a redux state/app.
 * token - getting token from a redux state/app.
 * @separator:
 */
const SignUpPage = (props: { disableCustomTheme?: boolean }) => {
  const query = new URLSearchParams(useLocation().search);
  const referal = query.get('ref');
  const initialValues: SignUpParams = {
    first_name: '',
    last_name: '',
    organization_name: '',
    email: '',
    password: '',
    confirm_password: '',
    agree_terms: false,
    special_link: referal ? referal : ''
  };
  const navigate = useNavigate();
  const theme = useTheme();
  const dispatch = useDispatch<AppDispatch>();
  const { token, isLoading } = useSelector((state: RootState) => state.app);

  /**
   * @heading3: Functions
   * @heading4: useEffect
   * @paragraph: Redirects the user to the dashboard if a valid token exists.
   */
  React.useEffect(() => {
    if (token) {
      navigate('/app/dashboard');
    }
  }, [token, navigate]);

  /**
   * @heading4: handleSubmit
   * @paragraph: Performs Signup functionality
   * @bold: Paramter
   * @ol:
   * values : SignUpParams - values are first_name, last_name, organisation_name, email and password
   */
  const handleSubmit = async (values: SignUpParams) => {
    if (values.email.trim() && values.password.trim() && values.first_name.trim() && values.last_name.trim() && values.organization_name.trim()) {
      try {
        await dispatch(signupThunk({
          first_name: values.first_name,
          last_name: values.last_name,
          organization_name: values.organization_name,
          email: values.email,
          password: values.password,
          special_link: referal ? referal : ''
        })).unwrap();
        navigate('/app/dashboard');
      } catch (e) {
        // error handle
      }
    }
  };
  return <AppTheme {...props}>
    <CssBaseline enableColorScheme />
    <StyledPage>
      <PageContainer>
        <ImageContainer position="relative">
          <Typography color="white" align="center" variant="h3" position="absolute" top="5rem" left="0" right="0" whiteSpace="wrap" width="50%">Put your client <br /> pipeline on autopilot</Typography>
          <Avatar
            key={theme.palette.mode}
            sx={{ width: 'auto', height: '70vh', position: 'absolute', bottom: '0', right: '0' }}
            src={ImageClass.auth}
            variant="square"
          />
        </ImageContainer>
        <FormContainer>
          <Avatar
            sx={{ width: 'auto', height: 60, bgcolor: 'white' }}
            src={theme.palette.mode === 'dark' ? ImageClass.logoDark : ImageClass.logoLight}
            variant="square"
          />
          <Typography variant="body1" fontSize="24px" fontWeight="600" marginBottom={4}>
            Create Your account
          </Typography>
          <Box display="flex"
            alignItems="center"
            width="100%">
            <Formik<SignUpParams>
              initialValues={initialValues}
              validationSchema={signupSchema}
              onSubmit={handleSubmit}
            >
              {({ values, handleChange, handleBlur, errors, touched, isValid }) => (
                <Form style={{ width: '100%' }}>
                  <StyledForm>
                    <Box display="flex" gap="10px">
                      <CustomInput
                        label="First Name"
                        placeholder="Enter your first name."
                        value={values.first_name}
                        onChange={handleChange('first_name')}
                        onBlur={handleBlur('first_name')}
                        error={touched.first_name && errors.first_name}
                        disabled={isLoading}
                        fullWidth
                      />
                      <CustomInput
                        label="Last Name"
                        placeholder="Enter your last name."
                        value={values.last_name}
                        onChange={handleChange('last_name')}
                        onBlur={handleBlur('last_name')}
                        error={touched.last_name && errors.last_name}
                        disabled={isLoading}
                        fullWidth
                      />
                    </Box>
                    <CustomInput
                      label="Organization Name"
                      placeholder="Enter your organization name."
                      value={values.organization_name}
                      onChange={handleChange('organization_name')}
                      onBlur={handleBlur('organization_name')}
                      error={touched.organization_name && errors.organization_name}
                      disabled={isLoading}
                      fullWidth
                    />
                    <CustomInput
                      label="Email"
                      placeholder="Enter your email."
                      value={values.email}
                      onChange={handleChange('email')}
                      onBlur={handleBlur('email')}
                      error={touched.email && errors.email}
                      disabled={isLoading}
                      fullWidth
                    />
                    <CustomInput
                      type="password"
                      label="Password"
                      placeholder="············"
                      value={values.password}
                      onChange={handleChange('password')}
                      onBlur={handleBlur('password')}
                      error={touched.password && errors.password}
                      disabled={isLoading}
                      fullWidth
                    />
                    <CustomInput
                      type="password"
                      label="Confirm Password"
                      placeholder="············"
                      value={values.confirm_password}
                      onChange={handleChange('confirm_password')}
                      onBlur={handleBlur('confirm_password')}
                      error={touched.confirm_password && errors.confirm_password}
                      disabled={isLoading}
                      fullWidth
                    />
                    <LabelWrapper marginBottom="0" label="Password must contain" />
                    <PasswordValidation password={values.password} />
                    <CustomInput
                      type="checkbox"
                      value={values.agree_terms}
                      label={
                        <>
                          I agree to{' '}
                          <span
                            style={{ color: '#1976d2', textDecoration: 'underline', cursor: 'pointer' }}
                            onClick={() => window.open('/term', '_blank')}
                          >
                            Terms and Conditions
                          </span>{' '}
                          &{' '}
                          <span
                            style={{ color: '#1976d2', textDecoration: 'underline', cursor: 'pointer' }}
                            onClick={() => window.open('/privacy', '_blank')}
                          >
                            Privacy Policy
                          </span>
                        </>
                      }
                      onChange={handleChange('agree_terms')}
                      onBlur={handleBlur('agree_terms')}
                      error={touched.agree_terms && errors.agree_terms}
                      disabled={isLoading}
                    />
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={isLoading || !isValid}
                      fullWidth
                    >
                      {isLoading && <CircularProgress
                        sx={{ color: 'white', marginRight: '15px' }}
                      />}
                      {isLoading ? 'Creating account' : 'Sign Up'}
                    </Button>
                    <Typography variant="body1" align="center">
                      Already have an account?{' '}
                      <StyledLink onClick={() => navigate('/login')}>
                        Login
                      </StyledLink>
                    </Typography>
                  </StyledForm>
                </Form>
              )}
            </Formik>
          </Box>
        </FormContainer>
      </PageContainer>
    </StyledPage>
    <ErrorModal />
    <RouteChangeToastCleaner />
  </AppTheme>;
};
export default SignUpPage;